<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <div class="mb-2 mb-md-0">
            <h1 class="h4 mb-1 font-weight-bold d-flex align-items-center flex-wrap">
              <span class="mr-2">
                {{ obj.id ? `Income receipt ${obj.incomeReceiptNumber || ""}` : "New income receipt" }}
              </span>
              <CBadge
                v-if="obj.id"
                :color="badgeColor('incomeReceipt', obj.statusDescription || 'Draft')"
                class="px-2 py-1"
              >
                {{ badgeLabel("incomeReceipt", obj.statusDescription || "Draft") }}
              </CBadge>
            </h1>
            <p class="text-muted mb-0">
              {{
                obj.id
                  ? "Edit the details, then save your changes"
                  : "Record money you received from a customer"
              }}
            </p>
          </div>
          <div class="d-flex align-items-center">
            <CDropdown
              placement="bottom-end"
              toggler-text="More actions"
              color="light"
              class="tour-cdropdown"
            >
              <CDropdownItem v-if="obj.id" @click="onDuplicate(obj)">Duplicate</CDropdownItem>
              <CDropdownItem @click="startTour">Take a tour</CDropdownItem>
            </CDropdown>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Main Form -->
    <CRow>
      <CCol lg="12">
        <CCard class="mb-4">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h6 font-weight-bold mb-0">Receipt details</h2>
          </CCardHeader>
          <CCardBody class="p-4">
            <CRow>
              <CCol md="6">
                <div ref="customerField" class="form-group pt-2" tabindex="-1">
                  <label for="income-receipt-customer">Customer *</label>
                  <v-select
                    input-id="income-receipt-customer"
                    class="form-control-lg"
                    :class="{ 'is-invalid': errors.customer }"
                    style="width: 100%; padding: 0"
                    v-model="selectedCustomer"
                    :label="'name'"
                    :options="customerItemsWithAddNew"
                    placeholder="Select customer"
                    @input="handleCustomerSelect"
                  />
                  <div v-if="errors.customer" class="invalid-feedback d-block">
                    {{ errors.customer }}
                  </div>
                </div>

                <div
                  v-if="selectedCustomer && selectedCustomer.id !== 'add_new'"
                  class="customer-details p-3 bg-light rounded mb-3"
                >
                  <div class="d-flex align-items-center mb-2">
                    <div class="c-avatar c-avatar-sm bg-primary text-white mr-2">
                      {{
                        selectedCustomer.name
                          ? selectedCustomer.name.charAt(0)
                          : "?"
                      }}
                    </div>
                    <div class="font-weight-bold">
                      {{ selectedCustomer.name }}
                    </div>
                  </div>
                  <div class="small text-muted mb-1" v-if="selectedCustomer.address">
                    <CIcon name="cil-location-pin" />
                    {{ selectedCustomer.address }}
                  </div>
                  <div class="small text-muted mb-1" v-if="selectedCustomer.contactEmail">
                    <CIcon name="cil-envelope-closed" />
                    {{ selectedCustomer.contactEmail }}
                  </div>
                  <div class="small text-muted" v-if="selectedCustomer.contactPhone">
                    <CIcon name="cil-phone" />
                    {{ selectedCustomer.contactPhone }}
                  </div>
                </div>
              </CCol>

              <CCol md="6">
                <CFormGroup wrapperClasses="input-group pt-2">
                  <template #label>Receipt number</template>
                  <template #input>
                    <CInput
                      placeholder="Auto-generated if left empty"
                      v-model="obj.incomeReceiptNumber"
                    />
                  </template>
                </CFormGroup>

                <div class="form-group pt-2">
                  <label for="income-receipt-date">Issue date *</label>
                  <CInput
                    id="income-receipt-date"
                    ref="issuedDateField"
                    type="date"
                    :value="computeIssuedDate"
                    :is-valid="errors.issuedDate ? false : null"
                    :invalid-feedback="errors.issuedDate"
                    @change="setIssuedDate"
                  />
                </div>

                <CFormGroup wrapperClasses="input-group pt-2">
                  <template #label>Payment method</template>
                  <template #input>
                    <CSelect :value.sync="obj.paymentMethod">
                      <option value="">Select payment method</option>
                      <option value="Cash">Cash</option>
                      <option value="Bank Transfer">Bank transfer</option>
                      <option value="Credit Card">Credit card</option>
                      <option value="Debit Card">Debit card</option>
                      <option value="Check">Cheque</option>
                      <option value="Online Banking">Online banking</option>
                      <option value="E-Wallet">E-wallet</option>
                      <option value="Other">Other</option>
                    </CSelect>
                  </template>
                </CFormGroup>
              </CCol>
            </CRow>

            <CFormGroup>
              <template #label>Receipt title</template>
              <template #input>
                <CTextarea
                  v-model="obj.title"
                  placeholder="Enter a descriptive title for this receipt"
                  rows="2"
                  @keydown="handleKeyDown($event, obj)"
                />
              </template>
            </CFormGroup>

            <!-- Receipt Items -->
            <InvoiceItemsComponent
              :items="incomeReceiptItems"
              :productItems="computeProductItemsWithAddNew"
              :grandTotal="grandTotal"
              :grandTaxOnly="0"
              :grandTotalWithTax="grandTotal"
              :selectedSalesTax="null"
              :salesTaxItems="[]"
              itemType="income-receipt"
              :showTax="false"
              :useDescriptionModal="true"
              @add-item="addNewItem"
              @remove-item="onRemoveClaimItem"
              @move-item="moveItem"
              @product-selected="handleProductSelect"
            />
            <div
              v-if="errors.items"
              ref="itemsField"
              tabindex="-1"
              class="invalid-feedback d-block"
            >
              {{ errors.items }}
            </div>

            <!-- Terms & Conditions -->
            <div class="mt-4">
              <CTextarea
                label="Terms and conditions"
                v-model="obj.note"
                placeholder="Enter your terms and conditions"
                rows="10"
              />
            </div>
          </CCardBody>
          <CCardFooter class="bg-transparent d-flex justify-content-between p-4">
            <CButton color="light" :disabled="saving" @click="cancel">Cancel</CButton>
            <CButton
              color="primary"
              class="tour-submit-button"
              :disabled="saving"
              @click="submit"
            >
              <CSpinner v-if="saving" size="sm" class="mr-1" />
              <CIcon v-else name="cil-check-circle" />
              {{ obj.id ? "Save" : "Create receipt" }}
            </CButton>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>

    <!-- Preview Section -->
    <CRow>
      <CCol lg="8">
        <CCard class="mb-4">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h6 font-weight-bold mb-0">Preview</h2>
          </CCardHeader>
          <CCardBody class="p-0">
            <div class="receipt-preview border rounded">
              <WidgetsReportIncomeReceipt :receipt="computedPreviewItem" />
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <!-- Sidebar -->
      <CCol lg="4">
        <CCard class="mb-4">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h6 font-weight-bold mb-0">Receipt summary</h2>
          </CCardHeader>
          <CCardBody class="p-4">
            <div class="d-flex justify-content-between mb-3">
              <span class="text-muted">Subtotal</span>
              <strong class="text-right">RM {{ formatMyr(grandTotal) }}</strong>
            </div>
            <hr class="my-3" />
            <div class="d-flex justify-content-between mb-3 h5">
              <span>Total</span>
              <strong class="text-right">RM {{ formatMyr(grandTotal) }}</strong>
            </div>
            <CButton color="light" block class="mt-3" @click="printReceipt">
              <CIcon name="cil-print" /> Print receipt
            </CButton>
            <CButton
              v-if="obj.id"
              color="primary"
              variant="outline"
              block
              class="mt-2"
              :disabled="downloading"
              @click="downloadPdf"
            >
              <CIcon name="cil-cloud-download" />
              {{ downloading ? "Preparing PDF…" : "Download PDF" }}
            </CButton>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Quick-add modals -->
    <CustomerFormModal
      :show="addCustomerFormPopup"
      :customer="itemAddNewCustomer"
      @close="addCustomerFormPopup = false"
      @save="onCustomerPopupConfirmation"
    />

    <ProductFormModal
      :show="addProductFormPopup"
      :product="itemAddNewProduct"
      @close="onProductPopupClose"
      @save="onProductPopupConfirmation"
    />
  </div>
</template>

<script>
import Shepherd from "shepherd.js";
import "shepherd.js/dist/css/shepherd.css";

import IncomeReceiptApi from "@/lib/incomeReceiptApi";
import CustomerApi from "@/lib/customerApi";
import ProductApi from "@/lib/productApi";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mytToday, toApiDate, asDateString } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";
import { badgeColor, badgeLabel } from "@/utils/statusBadges";
import WidgetsReportIncomeReceipt from "../widgets/WidgetsReportIncomeReceipt";
import InvoiceItemsComponent from "../components/InvoiceItemsComponent";
import CustomerFormModal from "../components/CustomerFormModal";
import ProductFormModal from "../components/ProductFormModal";

export default {
  name: "IncomeReceipt",
  components: {
    InvoiceItemsComponent,
    vSelect,
    WidgetsReportIncomeReceipt,
    CustomerFormModal,
    ProductFormModal,
  },
  data: () => {
    return {
      itemPendingNewProduct: null,
      itemAddNewProduct: {},
      addProductFormPopup: false,
      itemAddNewCustomer: {},
      addCustomerFormPopup: false,
      issuedDate: "",
      incomeReceiptItems: [],
      selectedItem: null,
      selectedCustomer: null,
      obj: {},
      submitted: false,
      saving: false,
      downloading: false,
      errors: {},
      api: new IncomeReceiptApi(),
      customerApi: new CustomerApi(),
      productApi: new ProductApi(),
      customerItems: [],
      productItems: [],
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Sales", "Receipts"]);
    this.refreshCustomer();
    this.refreshProduct();
    this.resetObj();
  },
  computed: {
    customerItemsWithAddNew() {
      return [
        ...this.customerItems,
        { id: "add_new", name: "+ Add new customer" },
      ];
    },
    computeProductItemsWithAddNew() {
      return [
        ...this.productItems,
        { id: "add_new", name: "+ Add new product" },
      ];
    },
    computedPreviewItem() {
      return {
        ...this.obj,
        items: this.computedIncomeReceiptItems,
      };
    },
    computeIssuedDate() {
      return asDateString(this.issuedDate);
    },
    computedIncomeReceiptItems() {
      return this.incomeReceiptItems.map((item) => {
        return {
          ...item,
          productName: item.product?.name || "",
          totalAmountPerItem: this.getTotalItemPrice(item),
        };
      });
    },
    grandTotal() {
      return this.incomeReceiptItems.reduce((total, item) => {
        return total + (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0);
      }, 0);
    },
  },
  methods: {
    formatMyr,
    badgeColor,
    badgeLabel,

    onDuplicate(item) {
      this.$router.push({
        path: `/tenants/incomeReceipt/${item.id}/duplicate`,
      });
    },

    handleCustomerSelect(selected) {
      if (selected?.id === "add_new") {
        this.addNewCustomer();
        this.selectedCustomer = null;
      } else {
        this.selectedCustomer = selected;
      }
      if (this.submitted) this.validate();
    },

    handleProductSelect(selected, item) {
      if (selected?.id === "add_new") {
        this.itemPendingNewProduct = item;
        this.itemAddNewProduct = {};
        this.addProductFormPopup = true;
      } else {
        item.product = selected;
      }
    },

    addNewCustomer() {
      this.itemAddNewCustomer = {};
      this.addCustomerFormPopup = true;
    },

    onCustomerPopupConfirmation(customer) {
      if (customer) {
        this.customerApi
          .create(customer)
          .then((response) => {
            const addedCustomer = response.result;
            this.refreshCustomer();
            this.selectedCustomer = addedCustomer;
            this.$toast.success("Customer added");
            if (this.submitted) this.validate();
          })
          .catch(({ data } = {}) => {
            this.$toast.error(`Couldn't add the customer. ${helper.getErrorMessage(data)}`);
          });
      }
      this.addCustomerFormPopup = false;
      this.itemAddNewCustomer = {};
    },

    onProductPopupConfirmation(product) {
      if (product) {
        const pending = this.itemPendingNewProduct;
        this.productApi
          .create(product)
          .then((response) => {
            const addedProduct = response.result;
            this.refreshProduct();
            if (pending) {
              pending.product = addedProduct;
            }
            this.$toast.success("Product added");
          })
          .catch(({ data } = {}) => {
            this.$toast.error(`Couldn't add the product. ${helper.getErrorMessage(data)}`);
          });
      } else if (this.itemPendingNewProduct) {
        this.itemPendingNewProduct.product = null;
      }
      this.addProductFormPopup = false;
      this.itemAddNewProduct = {};
      this.itemPendingNewProduct = null;
    },

    onProductPopupClose() {
      if (this.itemPendingNewProduct) {
        this.itemPendingNewProduct.product = null;
      }
      this.itemPendingNewProduct = null;
      this.addProductFormPopup = false;
    },

    // Item management
    addNewItem() {
      const newPosition = this.incomeReceiptItems.length + 1;
      this.incomeReceiptItems.push({
        id: this.generateGUID(),
        product: this.productItems.length > 0 ? this.productItems[0] : { name: "" },
        price: 0,
        quantity: 1,
        description: "",
        position: newPosition,
      });
    },

    onRemoveClaimItem(item) {
      const index = this.incomeReceiptItems.findIndex((i) => i.id === item.id);
      if (index > -1) {
        this.incomeReceiptItems.splice(index, 1);
        this.updatePositions();
      }
    },

    moveItem(index, direction) {
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex >= 0 && newIndex < this.incomeReceiptItems.length) {
        const temp = this.incomeReceiptItems[index];
        this.$set(this.incomeReceiptItems, index, this.incomeReceiptItems[newIndex]);
        this.$set(this.incomeReceiptItems, newIndex, temp);
        this.updatePositions();
      }
    },

    updatePositions() {
      this.incomeReceiptItems.forEach((item, index) => {
        item.position = index + 1;
      });
    },

    getTotalItemPrice(item) {
      const quantity = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.price) || 0;
      return quantity * price;
    },

    generateGUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        var r = (Math.random() * 16) | 0,
          v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    },

    setIssuedDate(e) {
      this.issuedDate = e;
      if (this.submitted) this.validate();
    },

    handleKeyDown(event, item) {
      const start = event.target.selectionStart;
      const end = event.target.selectionEnd;
      const field = item.description !== undefined ? "description" : "title";
      const current = item[field] || "";

      if (event.key === "Tab") {
        event.preventDefault();
        item[field] = current.substring(0, start) + "    " + current.substring(end);
        this.$nextTick(() => {
          event.target.selectionStart = event.target.selectionEnd = start + 4;
        });
      }

      if (event.key === "Enter") {
        item[field] = current.substring(0, start) + "\n" + current.substring(end);
        this.$nextTick(() => {
          event.target.selectionStart = event.target.selectionEnd = start + 1;
        });
      }
    },

    // Inline validation. Returns true when the receipt can be saved.
    validate() {
      const errors = {};
      const customer = this.selectedCustomer;
      if (!customer || customer.id === "add_new") {
        errors.customer = "Choose the customer who paid you.";
      }
      if (!asDateString(this.issuedDate)) {
        errors.issuedDate = "Enter the date you received the money.";
      }
      if (!this.incomeReceiptItems.length) {
        errors.items = "Add at least one item to the receipt.";
      } else if (this.incomeReceiptItems.some((i) => !i.product || !i.product.id || i.product.id === "add_new")) {
        errors.items = "Choose a product for every item, or remove the empty rows.";
      }
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },

    focusFirstInvalid() {
      this.$nextTick(() => {
        const order = [
          ["customer", "customerField"],
          ["issuedDate", "issuedDateField"],
          ["items", "itemsField"],
        ];
        const hit = order.find(([key]) => this.errors[key]);
        if (!hit) return;
        const ref = this.$refs[hit[1]];
        const el = ref && (ref.$el || ref);
        if (!el) return;
        if (el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
        const focusable = el.querySelector ? el.querySelector("input, [tabindex]") : null;
        const target = focusable || el;
        if (target.focus) target.focus();
      });
    },

    refreshCustomer() {
      this.customerApi
        .getListByCurrentBusiness()
        .then((response) => {
          this.customerItems = response.result;
        })
        .catch(() => {});
    },

    refreshProduct() {
      this.productApi
        .getListByCurrentBusiness()
        .then((response) => {
          this.productItems = response.result;
        })
        .catch(() => {});
    },

    resetObj() {
      var self = this;
      if (self.$route.params.id) {
        self.api
          .get(self.$route.params.id)
          .then((response) => {
            self.obj = response.result;
            self.issuedDate = self.obj.issuedDate;
            self.selectedCustomer = self.obj.customer;

            if (self.obj.items.length > 0) {
              if (self.obj.items[0].position == 0) {
                for (var i = 0; i < self.obj.items.length; i++) {
                  self.obj.items[i].position = i + 1;
                }
              }
            }
            self.incomeReceiptItems = self.obj.items;

            // Duplicate: same content, new number and today's date
            if (self.$route.name === "IncomeReceiptDuplicate") {
              delete self.obj.id;
              self.issuedDate = mytToday();

              self.api
                .getNextNumber()
                .then((response) => {
                  self.obj.incomeReceiptNumber = response.result;
                })
                .catch(() => {});

              self.obj.createdAt = null;
              self.obj.updatedAt = null;

              for (var j = 0; j < self.obj.items.length; j++) {
                delete self.obj.items[j].id;
              }

              self.$toast.info("You're editing a copy of the receipt");
            }
          })
          .catch(({ data } = {}) => {
            self.$toast.error(`Couldn't load the receipt. ${helper.getErrorMessage(data)}`);
          });
      } else {
        self.obj = self.getEmptyObj();
        self.issuedDate = mytToday();

        self.api
          .getNextNumber()
          .then((response) => {
            self.obj.incomeReceiptNumber = response.result;
          })
          .catch(() => {});
      }
    },

    onSubmit() {
      var self = this;
      if (self.saving) return;
      if (!self.validate()) {
        self.focusFirstInvalid();
        return;
      }
      self.saving = true;
      self.obj.items = self.computedIncomeReceiptItems;
      // Date-only field goes to the API as "yyyy-MM-dd" (never a Date: JSON turns it into UTC, 8 hours back)
      self.obj.issuedDate = toApiDate(asDateString(self.issuedDate)) || null;

      self.obj.items.forEach((item) => {
        item.productId = item.product.id;
      });

      if (self.selectedCustomer) {
        self.obj.customerId = self.selectedCustomer.id;
      }

      if (!self.obj.id) {
        this.api
          .create(self.obj)
          .then((response) => {
            self.$toast.success("Receipt created");
            var newObj = response.result;
            self.$router.push({
              path: `/tenants/incomeReceipt/${newObj.id}`,
            });
          })
          .catch(({ data } = {}) => {
            self.$toast.error(`Couldn't create the receipt. ${helper.getErrorMessage(data)}`);
          })
          .finally(() => {
            self.saving = false;
          });
      } else {
        this.api
          .update(self.obj)
          .then(() => {
            self.$toast.success("Receipt saved");
            self.resetObj();
          })
          .catch(({ data } = {}) => {
            self.$toast.error(`Couldn't save the receipt. ${helper.getErrorMessage(data)}`);
          })
          .finally(() => {
            self.saving = false;
          });
      }
    },

    startTour() {
      const tour = new Shepherd.Tour({
        useModalOverlay: true,
        cancelIcon: { enabled: true },
        defaultStepOptions: {
          scrollTo: { behavior: "smooth", block: "center" },
          classes: "shadow-md",
        },
      });

      tour.addStep({
        id: "receipt-header",
        text: "This is the receipt header where you can see the receipt number and manage actions.",
        attachTo: { element: ".tour-cdropdown", on: "bottom" },
        buttons: [{ text: "Next", action: tour.next }],
      });

      tour.addStep({
        id: "submit-button",
        text: "Click here to save your receipt.",
        attachTo: { element: ".tour-submit-button", on: "right" },
        buttons: [
          { text: "Back", action: tour.back },
          { text: "Finish", action: tour.complete },
        ],
      });

      tour.start();
    },

    printReceipt() {
      window.print();
    },

    downloadPdf() {
      if (!this.obj.id || this.downloading) return;
      this.downloading = true;
      const name = this.obj.incomeReceiptNumber ? `${this.obj.incomeReceiptNumber}.pdf` : undefined;
      this.api
        .downloadPDF(this.obj.id, name)
        .catch(() => {
          this.$toast.error("Couldn't download the PDF. Check your connection and try again.");
        })
        .finally(() => {
          this.downloading = false;
        });
    },

    cancel() {
      this.$router.push({ path: "/tenants/incomeReceiptList" });
    },

    getEmptyObj() {
      return {
        incomeReceiptNumber: "",
        title: "",
        note: "",
        paymentMethod: "Online Banking", // Default payment method
      };
    },

    submit() {
      this.submitted = true;
      this.onSubmit();
    },
  },
};
</script>

<style scoped>
.receipt-preview {
  transform: scale(0.8);
  transform-origin: top center;
  overflow: hidden;
}

@media (max-width: 767.98px) {
  .receipt-preview {
    transform: scale(0.7);
  }
}

.form-control-lg.is-invalid ::v-deep .vs__dropdown-toggle {
  border-color: var(--danger);
}
</style>
